import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getChhattisgarhDailyPeak,
  getChhattisgarhMonthlyPeakDiscom,
} from "../Utility/RestApi";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useDispatch, useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { formatDateForAPI } from "../Utility/const";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip from "../../../components/Metrics";
import { DiscomLoadMetrics } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";

const X_AXIS = [
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];
const UNITS = ["MW", "MWh", "LU"];
const GROUP_BY_OPTIONS = ["Discom Load", "Total Discom Load", "CSPDCL Drawal"];
const DISCOM_OPTIONS = ["CSPDCL"];
const TYPE_OPTIONS = ["Actual Load"];

// Helper: convert month number + year to "Mon-YY"
function getMonthWithYear(month: number, year: number): string {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${monthNames[month - 1]}-${year.toString().slice(-2)}`;
}

// Helper: parse "Mon-YY" back to { month, year }
function parseMonthYear(monthYearStr: string | undefined): {
  month: number;
  year: number;
} {
  if (!monthYearStr) return { month: 0, year: 0 };
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const parts = monthYearStr.split("-");
  if (parts.length !== 2) return { month: 0, year: 0 };
  const [monthStr, yearStr] = parts;
  return {
    month: monthNames.indexOf(monthStr) + 1,
    year: 2000 + parseInt(yearStr),
  };
}

interface ChhattisgarhPeakAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  selectedSuffix: any[];
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: any[];
  metrics: DiscomLoadMetrics | null;
}

export function ChhattisgarhPeakAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix,
  onTypeChange,
  chartSuffixes,
  metrics,
}: ChhattisgarhPeakAnalysisProps) {
  const [tableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [peakData, setPeakData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [loading, setLoading] = useState(true);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const _handleFetchData = useCallback(async () => {
    setLoading(true);
    try {
      const dates = DefaultDates[activeTab]?.defaultRangeDate;
      if (!dates?.startDate || !dates?.endDate) {
        setPeakData([]);
        return;
      }

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const raw =
        selectedXaxis.key === "month"
          ? await getChhattisgarhMonthlyPeakDiscom(startDateStr, endDateStr)
          : await getChhattisgarhDailyPeak(startDateStr, endDateStr);
      const toBlock = (v: any) =>
        v != null && v !== "" ? Math.round(Number(v)).toString() : "";
      const processed = (Array.isArray(raw) ? raw : []).map((item) => ({
        ...item,
        // For monthly view, convert month+year numbers to "Mon-YY" label
        ...(selectedXaxis.key === "month" && {
          month: getMonthWithYear(item.month, item.year),
          peak_slot: toBlock(item.timeblock_of_peak),
          morning_peak_slot: toBlock(item.timeblock_of_morning_peak),
          evening_peak_slot: toBlock(item.timeblock_of_evening_peak),
        }),
        // For daily view, convert slot values to integers
        ...(selectedXaxis.key !== "month" && {
          peak_slot: toBlock(item.peak_slot),
          morning_peak_slot: toBlock(item.morning_peak_slot),
          evening_peak_slot: toBlock(item.evening_peak_slot),
        }),
        peak_mw: parseFloat(item.peak_mw?.toString() ?? "0"),
        morning_peak_mw: parseFloat(item.morning_peak_mw?.toString() ?? "0"),
        evening_peak_mw: parseFloat(item.evening_peak_mw?.toString() ?? "0"),
        average_mw: parseFloat(item.average_mw?.toString() ?? "0"),
      }));

      setPeakData(processed);
    } catch (error) {
      console.error("Error fetching Chhattisgarh peak data:", error);
      setPeakData([]);
    } finally {
      setLoading(false);
    }
  }, [
    DefaultDates,
    activeTab,
    selectedXaxis.key,
    DefaultDates[activeTab]?.defaultRangeDate?.startDate,
    DefaultDates[activeTab]?.defaultRangeDate?.endDate,
  ]);

  useEffect(() => {
    _handleFetchData();
  }, [_handleFetchData]);

  // ─── Chart data derivation ────────────────────────────────────────────────
  useEffect(() => {
    if (peakData.length === 0) {
      setChartData([]);
      setSummaryData([]);
      setTableData([]);
      setChartKeys([]);
      return;
    }

    // Sort
    const sorted = [...peakData].sort((a, b) => {
      if (selectedXaxis.key === "month") {
        const am = parseMonthYear(a.month);
        const bm = parseMonthYear(b.month);
        return am.year !== bm.year ? am.year - bm.year : am.month - bm.month;
      }
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });

    // Unit conversion
    const factor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    const converted = sorted.map((item) => ({
      ...item,
      peak_mw: (item.peak_mw ?? 0) * factor,
      morning_peak_mw: (item.morning_peak_mw ?? 0) * factor,
      evening_peak_mw: (item.evening_peak_mw ?? 0) * factor,
      average_mw: (item.average_mw ?? 0) * factor,
      block: item.peak_slot,
    }));

    const par: ChartDataParams = {
      keys: selectedXaxis.values,
      tableKeys:
        selectedXaxis.key === "date"
          ? ["date", "peak_slot", "morning_peak_slot", "evening_peak_slot"]
          : ["month", "peak_slot", "morning_peak_slot", "evening_peak_slot"],
      groupBy: [],
      volumeKey: "",
      aggregationKeys: [
        "peak_mw",
        "morning_peak_mw",
        "evening_peak_mw",
        // "average_mw",
      ],
      averageKeys: [],
      averageDuplicationKeys: [],
      priceKey: "",
      data: converted,
    };

    const cData = FormatChartData(par);
    const xKey = selectedXaxis.key === "month" ? "month" : "date";
    const slotByKey = Object.fromEntries(
      converted.map((item) => [
        item[xKey],
        {
          peak_slot: item.peak_slot,
          morning_peak_slot: item.morning_peak_slot,
          evening_peak_slot: item.evening_peak_slot,
        },
      ]),
    );
    const mergedData = cData.data.map((item) => ({
      ...item,
      ...(slotByKey[item[xKey]] ?? {}),
      block: slotByKey[item[xKey]]?.peak_slot ?? "-",
    }));
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setSummaryData(mergedData);
    setChartKeys(cData.keys);
  }, [peakData, selectedXaxis, selectedUnit]);
  const chartLegends = useMemo(() => {
    const peakTypes = [
      { suffix: "_peak_mw", displayName: "Day Peak", blockKey: "peak_slot" },
      {
        suffix: "_morning_peak_mw",
        displayName: "Morning Peak",
        blockKey: "morning_peak_slot",
      },
      {
        suffix: "_evening_peak_mw",
        displayName: "Evening Peak",
        blockKey: "evening_peak_slot",
      },
      // { suffix: "_average_mw", displayName: "Average" },
    ];

    return peakTypes.map(({ suffix, displayName, blockKey }) => ({
      dataKey: suffix,
      name: displayName,
      blockKey,
      legendColor: undefined,
      stroke: undefined,
      type: selectedChartType.value,
      stackId: selectedChartType.stackId !== undefined ? suffix : undefined,
    }));
  }, [selectedChartType]);

  // Table legends: interleave block columns before each peak value
  const tableLegends = useMemo(() => {
    const peakTypes = [
      {
        suffix: "_peak_mw",
        displayName: "Day Peak",
        blockKey: "peak_slot",
        blockLabel: "Day Peak Block",
      },
      {
        suffix: "_morning_peak_mw",
        displayName: "Morning Peak",
        blockKey: "morning_peak_slot",
        blockLabel: "Morning Peak Block",
      },
      {
        suffix: "_evening_peak_mw",
        displayName: "Evening Peak",
        blockKey: "evening_peak_slot",
        blockLabel: "Evening Peak Block",
      },
    ];

    const legends: any[] = [];
    peakTypes.forEach(({ suffix, displayName, blockKey, blockLabel }) => {
      // Block column first
      legends.push({
        dataKey: blockKey,
        name: blockLabel,
        unit: "",
        legendColor: undefined,
        stroke: undefined,
        type: selectedChartType.value,
      });
      // Then the MW value column
      legends.push({
        dataKey: suffix,
        name: displayName,
        legendColor: undefined,
        stroke: undefined,
        type: selectedChartType.value,
      });
    });
    return legends;
  }, [selectedChartType]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(chartLegends);

  // ─── Summary helpers ──────────────────────────────────────────────────────
  const dateKey = selectedXaxis.key === "date" ? "date" : "month";
  const dateLabel = selectedXaxis.key === "date" ? "Date" : "Month";
  const xDataKey = selectedXaxis.values[0];

  const baseProps = {
    legends: visibleLegends as any,
    data: summaryData,
    tableData,
    unit: selectedUnit,
    filename: AddDatesWithFileName({
      fileName: "Discom Peak Analysis",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  // Summary: one Block column resolved per-legend via blockKey
  const summaryProps = {
    ...baseProps,
    extraKeys: [dateKey, "block"],
    extraLabels: [dateLabel, "Block"],
  };

  // Table: interleaved Block | MW pairs per peak type
  const tableProps = {
    ...baseProps,
    legends: tableLegends as any,
    extraKeys: [dateKey],
    extraLabels: [dateLabel],
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Peak Analysis"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(_, index) => {
              setSelectedXaxis(X_AXIS[index]);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => onUnitChange(item)}
          />
          {/* <FilterMenu
            title="Discom"
            items={DISCOM_OPTIONS}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={onDiscomChange}
            // disabled={true}
          /> */}
          <FilterMenu
            title="Type"
            items={TYPE_OPTIONS}
            selectedItems={selectedSuffix.map((s) => s.displayName)}
            setSelectedItems={onTypeChange}
            // disabled={true}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_, index) => setSelectedChartType(H_CHART_TYPES[index])}
          />

          {/* <FilterMenu
            title="Group By"
            items={GROUP_BY_OPTIONS}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => onGroupByChange(index)}
            disabled={true}
          /> */}
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChip
              icon={<PeakIcon width={20} height={19} />}
              items={[
                {
                  label: "Peak Load",
                  value: metrics["Peak Load (MW)"].value,
                  unit: metrics["Peak Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<AvgIcon width={20} height={19} />}
              items={[
                {
                  label: "Average Load",
                  value: metrics["Average Load (MW)"].value,
                  unit: metrics["Average Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<MinIcon width={20} height={19} />}
              items={[
                {
                  label: "Minimum Load",
                  value: metrics["Minimum Load (MW)"].value,
                  unit: metrics["Minimum Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<TotalIcon width={20} height={19} />}
              items={[
                {
                  label: "Total Energy",
                  value: metrics["Total Energy (MU)"].value,
                  unit: metrics["Total Energy (MU)"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Discom Peak Analysis"
              subtitle="Fetching Chhattisgarh discom peak analysis data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-100">
                  Discom Peak Analysis ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart") {
                        downloadChartTableCSV(tableProps);
                      } else if (dataTab.label === "Summary") {
                        downloadSummaryCSV(summaryProps);
                      } else if (dataTab.label === "Table") {
                        downloadChartTableCSV(tableProps);
                      }
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`${selectedUnit}-${selectedXaxis.key}-${selectedChartType.value}`}
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={chartLegends as any}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={xDataKey}
                  secondXDataKey={undefined}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...summaryProps} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tableProps} />}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>CSPDCL</p>}
              text=" 
                   1. Actual Load data is sourced from CSPDCL's official records."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
